import * as React from 'react';
import {BaseFieldProps, BaseFieldVariant} from '../../index';
import {useCallback, useMemo} from 'react';
import {useTheme} from '../../../../theme';
import {useIsDesktop} from '../../../../hooks/useIsDesktop';
import {createStyles, makeStyles, MuiThemeProvider, withStyles} from '@material-ui/core/styles';
import {Theme} from '../../../../theme/interface';
import {FormControl, FormHelperText, InputBase, InputLabel, MenuItem, Select} from '@material-ui/core';
import {Text} from '../../../text';
import {useStyles} from '../../../../hooks/styles';
import {DeviceSize} from '../../../../theme/helpers';
import {images} from '../../../../../assets/icons';
import {BaseFieldColorScheme, DEFAULT_BASE_FIELD_COLOR_SCHEME} from '../../model';

const useMenuStyles = makeStyles(() => ({
  paper: {
    border: (props: Theme) => `1px solid ${props.input.select.borderColor}`,
    borderBottomLeftRadius: (props: Theme) => props.input.common.borderRadius,
    borderBottomRightRadius: (props: Theme) => props.input.common.borderRadius,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    marginLeft: -1,
    backgroundColor: (props: Theme) => props.input.select.backgroundColor,
    boxShadow: 'none',
    fontSize: (props: Theme) => props.fonts.body.fontSize,
    lineHeight: '24px',
  },
}));

export const BaseFieldSelect: React.FC<BaseFieldProps> = (props) => {
  const {variant, readOnly, label, values, input, meta, hint, colorScheme} = props;
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const error: boolean = !!(meta?.touched && !!meta.error);

  const styles = useStyles(({theme: theme_, mediaOnly}) => {
    return {
      labelC: {
        ...mediaOnly(DeviceSize.desktop, theme_.fonts.body, {...theme_.fonts.bodySmallLight}),
        color: theme_.input.inputTextDisabled.color,
      },
    };
  });

  const menuStyles = useMenuStyles(theme);

  const labelComponent = useMemo(
    () => (
      <Text lineBreakMode='tail' numberOfLines={1} style={styles.labelC}>
        {label}
      </Text>
    ),
    [label, styles],
  );
  const handleChange = useCallback((e: React.ChangeEvent<{name?: string; value: unknown}>) => {
    input.onChange && input.onChange(e.target.value || null);
  }, []);

  const MenuItemComponent = useMemo(
    () => withStyles(createMenuItemStyles(theme))(MenuItem) as typeof MenuItem,
    [theme],
  );
  const StyledInputBase = useMemo(
    () => withStyles(createCustomStyles(theme, variant || 'standard', isDesktop, readOnly, colorScheme))(InputBase),
    [variant, isDesktop, readOnly, theme],
  );
  const StyledFormControl = useMemo(
    () => withStyles(createCustomStyles(theme, variant || 'standard', isDesktop, readOnly, colorScheme))(FormControl),
    [variant, isDesktop, readOnly, theme],
  );

  return (
    <MuiThemeProvider theme={theme.muiDefaultTheme}>
      <StyledFormControl fullWidth variant={variant} error={error}>
        <InputLabel id='select-theme-label'>{labelComponent}</InputLabel>
        <Select
          name={input.name}
          variant={variant}
          disabled={readOnly}
          value={input.value}
          label={labelComponent}
          onChange={handleChange}
          MenuProps={{
            PaperProps: {
              className: menuStyles.paper,
            },
            variant: 'selectedMenu',
            disableAutoFocus: true,
            disableAutoFocusItem: true,
            autoFocus: false,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
          }}
          IconComponent={images.ChevronUpDropdown}
          input={<StyledInputBase />}
          inputProps={{'data-testid': input.name}}>
          {(values || []).map((v) => (
            <MenuItemComponent key={v.id} value={v.value}>
              {v.value}
            </MenuItemComponent>
          ))}
        </Select>
        {error || hint ? <FormHelperText>{error ? meta?.error.error : hint}</FormHelperText> : null}
      </StyledFormControl>
    </MuiThemeProvider>
  );
};

const createMenuItemStyles = (theme: Theme) => () =>
  createStyles({
    root: {
      '&.MuiMenuItem-root': {
        padding: `${theme.input.common.paddingVertical}px ${theme.input.common.paddingHorizontal}px`,
        color: theme.color.textColor,
        lineHeight: '24px',
        fontSize: theme.fonts.body.fontSize,
      },
      '&.Mui-selected, &:hover, &.Mui-selected:hover': {
        backgroundColor: theme.input.common.borderColor,
        color: theme.input.select.hoverColor,
      },
    },
  });

const createCustomStyles =
  (
    theme: Theme,
    variant: BaseFieldVariant,
    isDesktop: boolean,
    readOnly = false,
    colorScheme: BaseFieldColorScheme = DEFAULT_BASE_FIELD_COLOR_SCHEME,
  ) =>
  () => {
    if (variant === 'outlined') {
      return createStyles({
        root: {
          '&.MuiInputBase-root': {
            borderRadius: theme.input.common.borderRadius,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: theme.input.select.borderColor,
            background: theme.input.select.backgroundColor,
            outline: 'none',
            lineHeight: '24px',
            fontSize: theme.fonts.body.fontSize,
            color: theme.color.textColor,
            '&.Mui-focused': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              '& .MuiSelect-icon': {
                color: theme.color.textColor,
              },
            },
            '&:focus': {
              border: '2px solid',
              borderColor: theme.input.common.borderColor,
            },
            '& .MuiSelect-icon': {
              color: theme.color.nonActiveCheckBoxColor,
            },
            '&.Mui-error': {
              borderColor: theme.input.common.errorColor,
            },
          },
          '& .MuiSelect-root': {
            padding: `${theme.input.common.paddingVertical}px ${theme.input.common.paddingHorizontal}px`,
            color: theme.color.textColor,
          },
          '& .MuiInputLabel-outlined': {
            paddingRight: 8,
          },
          '& .MuiFormLabel-filled': {
            display: 'none',
          },
          '& .MuiInputLabel-outlined:not(.Mui-focused):not(.MuiFormLabel-filled)': {
            transform: isDesktop ? 'translate(14px, 10px) scale(1)' : 'translate(14px, 7px) scale(1)',
          },
          '& .MuiInputLabel-outlined.Mui-focused': {
            transform: isDesktop ? 'translate(14px, 10px) scale(1)' : 'translate(14px, 7px) scale(1)',
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.input.common.borderColor,
          },
          '& .MuiFormLabel-root.Mui-focused div': {
            color: theme.color.textColor,
          },
          ...(readOnly
            ? {
                '&.Mui-disabled': {
                  color: theme.input.inputTextDisabled.color,
                  background: theme.input.colorScheme[colorScheme].backgroundColorDisabled,
                },
              }
            : {}),
        },
      });
    }

    return createStyles({
      root: {
        '&.MuiInputBase-root': {
          color: theme.color.textColor,
          lineHeight: '24px',
          padding: `${theme.input.common.paddingVertical}px ${theme.input.common.paddingHorizontal}px`,
          fontSize: theme.fonts.body.fontSize,
        },
        '& .MuiInput-underline:before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
      },
    });
  };
